/**
 * Mobile Navigation JavaScript
 * Handles mobile menu toggle, submenu expansion, and touch interactions
 */

class MobileNavigation {
  constructor() {
    this.overlay = document.getElementById('mobile-nav-overlay');
    this.toggleBtn = document.getElementById('mobile-nav-toggle');
    this.closeBtn = document.getElementById('mobile-nav-close');
    this.submenuToggles = document.querySelectorAll('.mobile-nav__toggle');
    this.taxonomyToggles = document.querySelectorAll('.mobile-nav__taxonomy-header');
    this.body = document.body;

    this.init();
  }

  init() {
    if (!this.overlay || !this.toggleBtn) return;

    this.bindEvents();
    this.setupSubmenuToggles();
    this.setupTaxonomyToggles();
    this.setupTouchEvents();
  }
  
  bindEvents() {
    // Toggle mobile nav
    this.toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });
    
    // Close mobile nav
    this.closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });
    
    // Close on overlay click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
    
    // Handle window resize
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024 && this.isOpen()) {
        this.close();
      }
    });
  }
  
  setupSubmenuToggles() {
    this.submenuToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSubmenu(toggle);
      });
    });
  }

  setupTaxonomyToggles() {
    this.taxonomyToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleTaxonomy(toggle);
      });
    });
  }

  setupTouchEvents() {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    
    // Touch events for swipe to close
    this.overlay.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    }, { passive: true });
    
    this.overlay.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
      
      // If swiping left and started from the left edge
      if (startX < 50 && currentX < startX - 50) {
        this.close();
        isDragging = false;
      }
    }, { passive: true });
    
    this.overlay.addEventListener('touchend', () => {
      isDragging = false;
    }, { passive: true });
  }
  
  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }
  
  open() {
    this.overlay.classList.add('active');
    this.body.style.overflow = 'hidden';
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    
    // Focus management
    this.closeBtn.focus();
    
    // Trap focus within mobile nav
    this.trapFocus();
  }
  
  close() {
    this.overlay.classList.remove('active');
    this.body.style.overflow = '';
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    
    // Return focus to toggle button
    this.toggleBtn.focus();
    
    // Remove focus trap
    this.removeFocusTrap();
  }
  
  isOpen() {
    return this.overlay.classList.contains('active');
  }
  
  toggleSubmenu(toggle) {
    const submenu = toggle.parentElement.nextElementSibling;
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    // Close all other submenus
    this.submenuToggles.forEach(otherToggle => {
      if (otherToggle !== toggle) {
        otherToggle.setAttribute('aria-expanded', 'false');
        const otherSubmenu = otherToggle.parentElement.nextElementSibling;
        if (otherSubmenu) {
          otherSubmenu.classList.remove('active');
        }
      }
    });

    // Toggle current submenu
    if (isExpanded) {
      toggle.setAttribute('aria-expanded', 'false');
      submenu.classList.remove('active');
    } else {
      toggle.setAttribute('aria-expanded', 'true');
      submenu.classList.add('active');
    }
  }

  toggleTaxonomy(toggle) {
    const taxonomyItems = toggle.parentElement.nextElementSibling;
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    // Toggle current taxonomy section
    if (isExpanded) {
      toggle.setAttribute('aria-expanded', 'false');
      taxonomyItems.classList.remove('active');
    } else {
      toggle.setAttribute('aria-expanded', 'true');
      taxonomyItems.classList.add('active');
    }
  }

  trapFocus() {
    const focusableElements = this.overlay.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    this.focusTrapHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };
    
    document.addEventListener('keydown', this.focusTrapHandler);
  }
  
  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

// Cart count updater
class CartCountUpdater {
  constructor() {
    this.cartCount = document.getElementById('mobile-cart-count');
    this.init();
  }
  
  init() {
    if (!this.cartCount) return;
    
    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      this.updateCount(e.detail.itemCount);
    });
  }
  
  updateCount(count) {
    if (this.cartCount) {
      this.cartCount.textContent = count;
      this.cartCount.style.display = count > 0 ? 'flex' : 'none';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MobileNavigation();
  new CartCountUpdater();
});

// Export for potential external use
window.MobileNavigation = MobileNavigation;
